/* eslint-disable @typescript-eslint/ban-types */ import { useEffect, useMemo, useState } from 'react';
import { onRender } from '../../intentTypes';
import { toHyphenCase } from '../../../util';
import { css as engineCss } from './css';
import { resolveTokens, useTokenContext } from './token';
import { isCssValue } from './value';
const AtRule = 'atRule.';
/**
 * Turns a media query object into a string that `matchMedia` can accept.
 *
 * @example
 * You can combine multiple media queries into a single rule by separating them with commas:
 * ```ts
 * input: {minQuery: css.value('600px'), maxWidth: css.value('1024px'))}
 * output: '(min-query: 600px) and (max-width: 1024px)'
 * ```
 *
 * @example
 * You can use tokens configured within the provided serializer:
 * ```ts
 * input: {minQuery: 'desktop'}
 * output: '(min-query: 1024px)'
 * ```
 */ function serializeMediaQueryList(serializer, assignedTokens, query) {
    const { token: tokenMap, options: { alias: aliases } } = serializer;
    return Object.entries(query).reduce((media, [key, valueOrToken])=>{
        let value;
        if (isCssValue(valueOrToken)) {
            value = valueOrToken.value;
        } else {
            const token = valueOrToken;
            const alias = aliases[`${AtRule}${key}`];
            const tokens = alias && tokenMap[alias];
            const cssVar = tokens && tokens[token];
            value = cssVar && resolveTokens(assignedTokens, cssVar);
        }
        const feature = value ? `(${toHyphenCase(key)}: ${value})` : '';
        return !media ? feature : [
            media,
            feature
        ].join(' and ');
    }, '');
}
/**
 * Adds the provided styles as an isolated dependency.
 */ function mediaDependency(...deps) {
    return {
        isolatedDependencies: deps.map(({ key })=>key)
    };
}
function mediaIntent(css, query, styles) {
    return onRender(()=>{
        const { tokens } = useTokenContext();
        const features = useMemo(()=>serializeMediaQueryList(css, tokens, query), [
            tokens
        ]);
        const [matches, setMatches] = useState(false);
        useEffect(()=>{
            const mq = window.matchMedia(features);
            const setMedia = (media)=>setMatches(media.matches);
            // Triggered at the first client-side load and if query changes
            setMedia(mq);
            // listen for the status of media query support changing.
            if (typeof mq.addEventListener === 'function') {
                mq.addEventListener('change', setMedia);
            } else {
                // Before Safari 14, MediaQueryList is based on EventTarget we must use addListener()
                mq.addListener(setMedia);
            }
            return ()=>{
                if (typeof mq.removeEventListener === 'function') {
                    mq.removeEventListener('change', setMedia);
                } else {
                    // Before Safari 14, MediaQueryList is based on EventTarget we must use removeListener()
                    mq.removeListener(setMedia);
                }
            };
        }, [
            setMatches,
            features
        ]);
        // media-query styles should be isolated dependencies to avoid
        // being "merged" with the component base styles
        return matches ? [
            css(mediaDependency(...styles))
        ] : [];
    });
}
export function unstable_createMediaIntent(css) {
    return (query, styles)=>mediaIntent(css, query, styles);
}
export const unstable_media = unstable_createMediaIntent(engineCss);

//# sourceMappingURL=media.js.map